import React, { useState, useEffect } from 'react';
import { Shield, Star, Clock, HeadphonesIcon, ExternalLink, Award } from 'lucide-react';
import { Card } from './ui/card';
import useInView from '../hooks/useInView';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations/translations';

const TestimonialsCarousel = () => {
  const { language } = useLanguage();
  const [position, setPosition] = useState(0);
  const [carouselRef, carouselInView] = useInView({ threshold: 0.2 });
  
  const testimonials = [
    {
      author: "EmmanuelPhilippe",
      textKey: "collaboration.testimonials.reviews.review1"
    },
    {
      author: "noejoel",
      textKey: "collaboration.testimonials.reviews.review2"
    },
    {
      author: "selmancb",
      textKey: "collaboration.testimonials.reviews.review3"
    },
    {
      author: "guyflorent",
      textKey: "collaboration.testimonials.reviews.review4"
    },
    {
      author: "Invité",
      textKey: "collaboration.testimonials.reviews.review5"
    },
    {
      author: "SafwaanePatel",
      textKey: "collaboration.testimonials.reviews.review6"
    },
    {
      author: "SafwaanePatel",
      textKey: "collaboration.testimonials.reviews.review7"
    },
    {
      author: "jbgiraud007",
      textKey: "collaboration.testimonials.reviews.review8"
    },
    {
      author: "Invité",
      textKey: "collaboration.testimonials.reviews.review9"
    },
    {
      author: "drissbenichou",
      textKey: "collaboration.testimonials.reviews.review10"
    },
    {
      author: "FouadZaher",
      textKey: "collaboration.testimonials.reviews.review11"
    },
    {
      author: "BenSanon",
      textKey: "collaboration.testimonials.reviews.review12"
    },
    {
      author: "OusmaneNdiaye2",
      textKey: "collaboration.testimonials.reviews.review13"
    },
    {
      author: "wallacemasada",
      textKey: "collaboration.testimonials.reviews.review14"
    }
  ];

  // Animation continue du défilement
  useEffect(() => {
    const interval = setInterval(() => {
      setPosition((prev) => {
        const cardWidth = 336; // 320px + 16px margin
        const totalWidth = testimonials.length * cardWidth;
        const newPosition = prev - 1;
        
        // Reset quand on atteint la fin de la première série
        if (Math.abs(newPosition) >= totalWidth) {
          return 0;
        }
        return newPosition;
      });
    }, 50); // 50ms pour un défilement fluide

    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="mb-16 pb-8 relative w-full overflow-hidden">
      {/* Titre en typo manuscrite */}
      <h3 className="text-4xl md:text-5xl text-black text-center mb-12" style={{ fontFamily: 'Caveat, cursive' }}>
        {getTranslation(language, 'collaboration.testimonials.title')}
      </h3>
      
      <div 
        ref={carouselRef}
        className="overflow-hidden py-4 mb-8"
      >
        <div 
          className="flex"
          style={{ 
            transform: `translateX(${position}px)`,
            transition: 'none',
            willChange: 'transform'
          }}
        >
          {/* Dupliquer les témoignages pour un défilement infini */}
          {[...testimonials, ...testimonials, ...testimonials].map((testimonial, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-80 mx-2 bg-white rounded-xl p-6 shadow-md hover:shadow-xl hover:scale-105 hover:-translate-y-2 border border-gray-200 hover:border-[#DC1A5F]/30 flex flex-col"
              style={{
                transform: carouselInView 
                  ? 'translateY(0) scale(1)' 
                  : 'translateY(50px) scale(0.9)',
                opacity: carouselInView ? 1 : 0,
                filter: carouselInView ? 'blur(0px)' : 'blur(5px)',
                transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transitionDelay: `${(index % 14) * 60}ms`,
                minHeight: '200px'
              }}
            >
              {/* Étoiles */}
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              
              {/* Citation - flex-grow pour prendre l'espace disponible */}
              <p className="text-gray-700 text-sm leading-relaxed mb-4 flex-grow">
                "{getTranslation(language, testimonial.textKey)}"
              </p>
              
              {/* Auteur - toujours en bas grâce au flex-grow sur la citation */}
              <p className="text-gray-900 font-semibold text-sm border-t border-gray-100 pt-3 mt-auto">
                {testimonial.author}
              </p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Lien vers profil ComeUp */}
      <div className="text-center">
        <a 
          href="https://comeup.com/fr/service/129092/designer-votre-application-mobile-dans-une-maquette-ui-ux-complete" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-black font-semibold text-base hover:text-[#DC1A5F] transition-colors duration-300 group"
        >
          <span>{getTranslation(language, 'collaboration.testimonials.link')}</span>
          <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  );
};

const PerformanceCarousel = ({ stats }) => {
  const { language } = useLanguage();
  const [statsRef, statsInView] = useInView({ threshold: 0.3 }); // Se déclenche quand 30% visible

  // Extraire les chiffres et textes des labels avec traduction
  const getStatData = (labelKey) => {
    if (labelKey === 'stat1') return { 
      number: '+14', 
      text: getTranslation(language, 'collaboration.stats.stat1.text')
    };
    if (labelKey === 'stat2') return { 
      number: '+150', 
      text: getTranslation(language, 'collaboration.stats.stat2.text')
    };
    if (labelKey === 'stat3') return { 
      number: '5/5', 
      text: getTranslation(language, 'collaboration.stats.stat3.text')
    };
    
    return { number: '', text: labelKey };
  };

  // Définir les couleurs pour chaque stat
  const getColorClass = (labelKey) => {
    if (labelKey === 'stat1') return 'text-[#32D6E0]'; // Bleu
    if (labelKey === 'stat2') return 'text-[#DC1A5F]'; // Rose
    if (labelKey === 'stat3') return 'text-[#32D6E0]'; // Bleu
    return 'text-white';
  };

  return (
    <div className="text-center mb-0 relative py-16 bg-black w-full">
      <div className="max-w-7xl mx-auto px-4">
        <div 
          ref={statsRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12"
          style={{ perspective: '1000px' }}
        >
          {stats.map((stat, index) => {
            const statData = getStatData(stat.labelKey);
            const colorClass = getColorClass(stat.labelKey);
            
            return (
              <div
                key={index}
                className={`flex flex-col items-center justify-center py-8 group transition-all duration-1200 ${
                  statsInView
                    ? 'opacity-100'
                    : 'opacity-0'
                }`}
                style={{
                  transform: statsInView 
                    ? 'translateY(0) translateZ(0) rotateX(0deg) rotateY(0deg) scale(1)' 
                    : 'translateY(100px) translateZ(-200px) rotateX(45deg) rotateY(15deg) scale(0.7)',
                  transitionDelay: statsInView ? `${index * 150}ms` : '0ms',
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Glow effect background */}
                <div 
                  className={`absolute inset-0 rounded-2xl blur-2xl transition-opacity duration-1000 ${
                    statsInView ? 'opacity-30' : 'opacity-0'
                  }`}
                  style={{
                    background: stat.labelKey === 'stat2' ? '#DC1A5F' : '#32D6E0',
                    transitionDelay: statsInView ? `${index * 150 + 300}ms` : '0ms'
                  }}
                ></div>

                {/* Contenu */}
                <div className="space-y-3 relative z-10">
                  {/* Chiffre géant avec effet pulse */}
                  <div 
                    className={`text-6xl md:text-7xl lg:text-8xl font-black tracking-tight ${colorClass} transition-all duration-500 group-hover:scale-125 group-hover:rotate-3`}
                    style={{
                      filter: statsInView ? 'blur(0px)' : 'blur(10px)',
                      transitionDelay: statsInView ? `${index * 150 + 200}ms` : '0ms',
                      textShadow: `0 0 40px ${stat.labelKey === 'stat2' ? '#DC1A5F' : '#32D6E0'}80`
                    }}
                  >
                    {statData.number}
                  </div>

                  {/* Texte descriptif */}
                  <div 
                    className="text-lg md:text-xl lg:text-2xl font-light text-white transition-all duration-700"
                    style={{
                      transform: statsInView ? 'translateY(0)' : 'translateY(20px)',
                      opacity: statsInView ? 1 : 0,
                      transitionDelay: statsInView ? `${index * 150 + 400}ms` : '0ms'
                    }}
                  >
                    {statData.text}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CollaborationSection = () => {
  const [titleRef, titleInView] = useInView();
  const [benefitsRef, benefitsInView] = useInView();
  const [platformRef, platformInView] = useInView();
  const { language } = useLanguage();

  const benefits = [
    {
      icon: <Shield className="w-6 h-6 icon-primary" />,
      titleKey: "collaboration.benefits.benefit1.title",
      descriptionKey: "collaboration.benefits.benefit1.description",
      fillColor: "from-[#DC1A5F]/20 to-[#DC1A5F]/40"
    },
    {
      icon: <Award className="w-6 h-6 icon-secondary" />,
      titleKey: "collaboration.benefits.benefit2.title",
      descriptionKey: "collaboration.benefits.benefit2.description",
      fillColor: "from-[#32D6E0]/20 to-[#32D6E0]/40"
    },
    {
      icon: <HeadphonesIcon className="w-6 h-6 icon-primary" />,
      titleKey: "collaboration.benefits.benefit3.title",
      descriptionKey: "collaboration.benefits.benefit3.description",
      fillColor: "from-[#DC1A5F]/20 to-[#DC1A5F]/40"
    },
    {
      icon: <Star className="w-6 h-6 icon-secondary" />,
      titleKey: "collaboration.benefits.benefit4.title",
      descriptionKey: "collaboration.benefits.benefit4.description",
      fillColor: "from-[#32D6E0]/20 to-[#32D6E0]/40"
    }
  ];

  const stats = [
    {
      icon: <Award className="w-5 h-5 icon-primary" />,
      labelKey: "stat1",
      fillColor: "from-[#DC1A5F]/20 to-[#DC1A5F]/40"
    },
    {
      icon: <Award className="w-5 h-5 icon-secondary" />,
      labelKey: "stat2",
      fillColor: "from-[#32D6E0]/20 to-[#32D6E0]/40"
    },
    {
      icon: <Star className="w-5 h-5 icon-primary" />,
      labelKey: "stat3",
      fillColor: "from-[#DC1A5F]/20 to-[#DC1A5F]/40"
    }
  ];

  return (
    <section id="collaboration" className="relative overflow-hidden">
      {/* Fond blanc */}
      <div className="absolute inset-0 bg-white"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-24 pb-0">
        
        {/* Section Header */}
        <div 
          ref={titleRef}
          className={`text-center mb-20 transition-all duration-1000 ${
            titleInView 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="gradient-text">{getTranslation(language, 'collaboration.title')}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed mb-4">
            {getTranslation(language, 'collaboration.subtitle')}
          </p>
          
          <h3 className="text-2xl font-bold text-black mt-8">{getTranslation(language, 'collaboration.whyTitle')}</h3>
        </div>

        {/* Layout moderne en 2 colonnes */}
        <div 
          ref={platformRef}
          className={`grid lg:grid-cols-2 gap-12 mb-16 transition-all duration-1000 ${
            platformInView 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Colonne gauche - Avantages */}
          <div 
            ref={benefitsRef}
            className="space-y-4"
          >
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group bg-black border border-gray-800 rounded-2xl p-6 hover:border-[#DC1A5F]/50 transition-all duration-700 hover:scale-105 hover:-translate-y-1 hover:shadow-2xl"
                style={{
                  transform: benefitsInView 
                    ? 'translateX(0) translateY(0) rotateY(0deg) scale(1)' 
                    : 'translateX(-100px) translateY(30px) rotateY(-15deg) scale(0.85)',
                  opacity: benefitsInView ? 1 : 0,
                  filter: benefitsInView ? 'blur(0px)' : 'blur(5px)',
                  transitionDelay: benefitsInView ? `${index * 150}ms` : '0ms',
                  transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl group-hover:from-[#DC1A5F]/20 group-hover:to-[#32D6E0]/20 transition-all duration-500">
                    {benefit.icon}
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-white text-lg mb-1 group-hover:text-[#DC1A5F] transition-colors">
                      {getTranslation(language, benefit.titleKey)}
                    </h5>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {getTranslation(language, benefit.descriptionKey)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Colonne droite - Plateforme ComeUp */}
          <div 
            className="relative h-full transition-all duration-1000"
            style={{
              transform: platformInView 
                ? 'translateX(0) translateY(0) rotateY(0deg) scale(1)' 
                : 'translateX(100px) translateY(30px) rotateY(15deg) scale(0.85)',
              opacity: platformInView ? 1 : 0,
              filter: platformInView ? 'blur(0px)' : 'blur(5px)',
              transitionDelay: platformInView ? '300ms' : '0ms',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
              transformStyle: 'preserve-3d',
              perspective: '1000px'
            }}
          >
            <div className="bg-black border border-gray-800 rounded-3xl p-8 md:p-10 shadow-2xl h-full flex flex-col justify-between hover:scale-105 hover:-translate-y-2 transition-all duration-500">
              <div>
                <h3 className="text-3xl font-bold text-white mb-4">
                  ComeUp
                </h3>
                
                <p className="text-gray-300 text-base leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: getTranslation(language, 'collaboration.platform.description') }}>
                </p>

                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <span className="text-white font-semibold">4.3/5</span>
                  <span className="text-gray-400 text-sm">{getTranslation(language, 'collaboration.platform.trustpilot')}</span>
                </div>

                <a 
                  href="#" 
                  className="inline-flex items-center space-x-2 text-[#32D6E0] hover:text-[#DC1A5F] transition-colors duration-300 group mb-6"
                >
                  <span className="text-sm font-medium">{getTranslation(language, 'collaboration.platform.reviewsLink')}</span>
                  <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Avantages collaboration */}
              <div className="space-y-4 pt-8 border-t border-gray-800 mt-auto">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#DC1A5F]/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#DC1A5F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-300 text-sm leading-snug">{getTranslation(language, 'collaboration.platform.advantages.tracking')}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#32D6E0]/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#32D6E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-300 text-sm leading-snug">{getTranslation(language, 'collaboration.platform.advantages.interaction')}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#DC1A5F]/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#DC1A5F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-300 text-sm leading-snug">{getTranslation(language, 'collaboration.platform.advantages.voice')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note alternative */}
        <div className="text-center mb-16">
          <div className="inline-block bg-black border border-gray-800 rounded-2xl px-8 py-4">
            <p className="text-gray-300 text-base" dangerouslySetInnerHTML={{ __html: getTranslation(language, 'collaboration.alternativeNote') }}>
            </p>
          </div>
        </div>

      </div>

      {/* Carrousel d'avis clients - Full Width */}
      <TestimonialsCarousel />

      {/* Performance avec carrousel automatique - Full Width */}
      <PerformanceCarousel stats={stats} />
      
    </section>
  );
};

export default CollaborationSection;